import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { WarningCircle } from '@phosphor-icons/react'

export interface ErrorStateProps {
  title?: string
  description?: string
  error?: Error | string | null
  onRetry?: () => void
  onBack?: () => void
  retryLabel?: string
  backLabel?: string
  fullScreen?: boolean
  className?: string
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'Try again or contact support if the issue persists.',
  error,
  onRetry,
  onBack,
  retryLabel = 'Try again',
  backLabel = 'Go back',
  fullScreen = false,
  className,
}: ErrorStateProps) {
  const errorMessage = typeof error === 'string' ? error : error?.message

  return (
    // The enter keyframes only define a `from` state, so a hidden base class
    // plus fill-mode-forwards animates 0 -> 0 and the element never shows.
    // Elements must be visible at rest; fill-mode-backwards holds the `from`
    // state through each delay.
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center text-center',
        'duration-500 animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-backwards',
        fullScreen ? 'min-h-[80vh]' : 'py-16',
        className,
      )}
    >
      <div className="max-w-md">
        <div
          className={cn(
            'mx-auto flex h-16 w-16 items-center justify-center rounded-full',
            'shadow-sm ring-1 ring-border/50',
            'bg-destructive/10 text-destructive',
            'delay-100 duration-500 animate-in fade-in-0 zoom-in-90 fill-mode-backwards',
          )}
        >
          <WarningCircle className="h-7 w-7" />
        </div>
        <h3
          className={cn(
            'mt-5 text-xl font-semibold tracking-tight',
            'delay-200 duration-500 animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-backwards',
          )}
        >
          {title}
        </h3>
        <p
          className={cn(
            'mx-auto mt-3 max-w-sm text-sm font-normal leading-relaxed text-muted-foreground',
            'delay-300 duration-500 animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-backwards',
          )}
        >
          {errorMessage ?? description}
        </p>
        {(onRetry || onBack) && (
          <div
            className={cn(
              'mt-6 flex justify-center gap-3',
              'delay-400 duration-500 animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-backwards',
            )}
          >
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                {backLabel}
              </Button>
            )}
            {onRetry && <Button onClick={onRetry}>{retryLabel}</Button>}
          </div>
        )}
      </div>
    </div>
  )
}
